import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { ToolBarItem } from '@sdflc/ui';

import {
  logger,
  OperationManager,
  OPERATIONS,
  onlyPropsOfObject,
} from '../../../utils';
import { ACTIONS_BUTTONS, ACTIONS_IDS } from 'config';
import { useApiUserAllowListItem } from '../../../graphql/hooks';
import { LocalizationContext } from '../../../contexts';

import { userAllowListItemForm } from './userAllowListItemForm';

const BUTTONS = ACTIONS_BUTTONS({ withNames: true });

const tplNewUserAllowListItem = {
  allowedUserId: null,
  status: 100,
};

const useUserAllowListItems = () => {
  const { getText } = useContext(LocalizationContext);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const {
    userAllowListItem,
    userAllowListItems,
    userAllowListItemList,
    userAllowListItemCreate,
    userAllowListItemUpdate,
    userAllowListItemRemoveMany,
    userAllowListItemsOpsManager,
  } = useApiUserAllowListItem();

  const contactId = formData?.id || '';
  const createOp = userAllowListItemsOpsManager.getCreateOp();
  const updateOp = userAllowListItemsOpsManager.get(
    contactId,
    OPERATIONS.UPDATE
  );
  const saveOp = contactId ? updateOp : createOp;
  const saveOpResult = saveOp.result;
  const updatingOps = userAllowListItemsOpsManager.getOpValues(
    OPERATIONS.UPDATE
  );
  const removingOps = userAllowListItemsOpsManager.getOpValues(
    OPERATIONS.REMOVE_MANY
  );

  useEffect(() => {
    setOpen(!!formData);
  }, [formData]);

  useEffect(() => {
    // This effect should close the app drawer on successfull saving of new item

    // Item is not being saved yet
    if (!createOp.called) {
      return;
    }

    // Create operation result is not in progress and succeeded
    if (!createOp.result.isInProgress() && createOp.result.didSucceed()) {
      userAllowListItemsOpsManager.reset(
        OperationManager.defaultContext,
        OPERATIONS.CREATE
      );
      setFormData(null);
    }
  }, [createOp, userAllowListItemsOpsManager]);

  useEffect(() => {
    // This effect should close the app drawer on successfull saving of exising item

    // Item is not being saved yet
    if (!updateOp.called) {
      return;
    }

    // Operation result is not in progress and succeeded
    if (!updateOp.result.isInProgress() && updateOp.result.didSucceed()) {
      userAllowListItemsOpsManager.reset(contactId, OPERATIONS.UPDATE);
      setFormData(null);
    }
  }, [updateOp, userAllowListItemsOpsManager, contactId]);

  const handleClickAction = useCallback(
    async (action, item) => {
      switch (action.id) {
        default:
          logger.warn('The "useUserAllowListItems" got an unexpected action:', {
            action,
            item,
          });
          break;

        case ACTIONS_IDS.ADD:
        case ACTIONS_IDS.CREATE:
          userAllowListItemsOpsManager.reset(
            OperationManager.defaultContext,
            OPERATIONS.SET
          );
          setFormData(cloneDeep(tplNewUserAllowListItem));
          break;

        case ACTIONS_IDS.SAVE:
          if (!item.id) {
            userAllowListItemCreate({ params: item });
          } else {
            userAllowListItemUpdate({
              where: { id: item.id },
              params: onlyPropsOfObject(item, tplNewUserAllowListItem),
            });
          }
          break;

        case ACTIONS_IDS.CANCEL:
          setFormData(null);
          break;

        case ACTIONS_IDS.EDIT:
          userAllowListItemsOpsManager.reset(
            OperationManager.defaultContext,
            OPERATIONS.UPDATE
          );
          setFormData(cloneDeep(item));
          break;

        case ACTIONS_IDS.REMOVE:
          userAllowListItemRemoveMany({ where: [{ id: item.id }] });
          break;

        case ACTIONS_IDS.TOGGLE_STATUS:
          {
            const toUpd = onlyPropsOfObject(item, tplNewUserAllowListItem);
            toUpd.status = item.status === 100 ? 0 : 100;
            userAllowListItemUpdate({
              where: { id: item.id },
              params: toUpd,
            });
          }
          break;
      }
    },
    [
      userAllowListItemCreate,
      userAllowListItemUpdate,
      userAllowListItemRemoveMany,
      userAllowListItemsOpsManager,
    ]
  );

  const handleClickClose = useCallback(() => {
    handleClickAction({ id: ACTIONS_IDS.CANCEL }, null);
  }, [handleClickAction]);

  const formCfg = useMemo(() => {
    return {
      sections: userAllowListItemForm,
      labels: getText('blocks.userAllowListItem.form.labels'),
      hints: getText('pages.userAllowListItem.form.hints'),
      placeholders: getText('pages.userAllowListItem.form.placeholders'),
    };
  }, [getText]);

  const formActions = useMemo(() => [BUTTONS.SAVE, BUTTONS.CANCEL], []);
  const actionInProgress = useMemo(
    () => (saveOpResult.isInProgress() ? [ACTIONS_IDS.SAVE] : []),
    [saveOpResult]
  );

  const toolBarItems = useMemo(() => {
    return [
      // {
      //   id: 'keyword',
      //   type: ToolBarItem.types.COMPONENT,
      //   name: 'keyword',
      //   value: filters.keyword,
      //   component: Input,
      //   placeholder: getText('generic.search'),
      //   leftIcon: 'Search',
      // },
      { ...BUTTONS.ADD, type: ToolBarItem.types.BUTTON },
    ];
  }, []);

  return {
    open,
    userAllowListItem,
    userAllowListItems,
    userAllowListItemList,
    formData,
    formCfg,
    formActions,
    setFormData,
    handleClickAction,
    handleClickClose,
    userAllowListItemsOpsManager,
    actionInProgress,
    toolBarItems,
    saveOp,
    saveOpResult,
    updatingOps,
    removingOps,
  };
};

export { useUserAllowListItems };
